import React, { useState } from 'react';
import Header from './components/Header';
import SummaryBand from './components/SummaryBand';
import TabBar from './components/TabBar';
import Login from './components/Login';
import ExecutiveOverview from './components/tabs/ExecutiveOverview';
import ManpowerGap from './components/tabs/ManpowerGap';
import PMPenalty from './components/tabs/PMPenalty';
import CMPenalty from './components/tabs/CMPenalty';
import TowerExecutiveOverview from './components/tabs/TowerExecutiveOverview';
import TowerPM from './components/tabs/TowerPM';
import TowerCM from './components/tabs/TowerCM';
import MiscExecutiveOverview from './components/tabs/MiscExecutiveOverview';
import MiscPM from './components/tabs/MiscPM';
import MiscCM from './components/tabs/MiscCM';
import CooDashboardContent from './views/coo-dashboard/CooDashboardContent';
import BubbleShell from './views/chatbot/components/BubbleShell';
import {
  AUTH_KEY,
  AUTH_TARGETS,
  AUTH_TARGET_KEY,
  COO_DASHBOARD_PATH,
} from './constants/authConfig';
import { DEFAULT_MONTH, resolveMonth } from './constants/monthConfig';
import { DEFAULT_DOMAIN, DOMAINS, DOMAIN_KEY, resolveDomain } from './constants/domainConfig';

const MONTH_KEY = 'aerial_month';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      return window.sessionStorage.getItem(AUTH_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    try {
      return resolveMonth(window.sessionStorage.getItem(MONTH_KEY));
    } catch {
      return DEFAULT_MONTH;
    }
  });
  const [activeDomain, setActiveDomain] = useState(() => {
    try {
      return resolveDomain(window.sessionStorage.getItem(DOMAIN_KEY));
    } catch {
      return DEFAULT_DOMAIN;
    }
  });
  const [activeTab, setActiveTab] = useState(0);

  const domainConfig = DOMAINS[activeDomain];
  const tabs = domainConfig.tabs;

  const renderTab = () => {
    const tabKey = `${activeDomain}-${selectedMonth}-${activeTab}`;

    if (activeDomain === 'executive') {
      // The canonical COO Dashboard, rendered inline. Same component used
      // standalone at /coo-dashboard (via CooDashboardPage). Hero hidden
      // here since the SummaryBand above already provides the page header.
      // FTTX domain cards excluded on this inline view only.
      return (
        <CooDashboardContent
          key={tabKey}
          showHero={false}
          excludeDomainTags={['FTTX']}
        />
      );
    }

    if (activeDomain === 'fibre') {
      switch (activeTab) {
        case 0: return <ExecutiveOverview key={tabKey} onNavigateTab={setActiveTab} selectedMonth={selectedMonth} />;
        case 1: return <ManpowerGap key={tabKey} selectedMonth={selectedMonth} />;
        case 2: return <PMPenalty key={tabKey} selectedMonth={selectedMonth} />;
        case 3: return <CMPenalty key={tabKey} selectedMonth={selectedMonth} />;
        default: return <ExecutiveOverview key={tabKey} onNavigateTab={setActiveTab} selectedMonth={selectedMonth} />;
      }
    }

    if (activeDomain === 'tower') {
      switch (activeTab) {
        case 0: return <TowerExecutiveOverview key={tabKey} onNavigateTab={setActiveTab} selectedMonth={selectedMonth} />;
        case 1: return <TowerPM key={tabKey} selectedMonth={selectedMonth} />;
        case 2: return <TowerCM key={tabKey} selectedMonth={selectedMonth} />;
        default: return <TowerExecutiveOverview key={tabKey} onNavigateTab={setActiveTab} selectedMonth={selectedMonth} />;
      }
    }

    if (activeDomain === 'misc') {
      switch (activeTab) {
        case 0: return <MiscExecutiveOverview key={tabKey} onNavigateTab={setActiveTab} selectedMonth={selectedMonth} />;
        case 1: return <MiscPM key={tabKey} selectedMonth={selectedMonth} />;
        case 2: return <MiscCM key={tabKey} selectedMonth={selectedMonth} />;
        default: return <MiscExecutiveOverview key={tabKey} onNavigateTab={setActiveTab} selectedMonth={selectedMonth} />;
      }
    }

    return null;
  };

  const persistMonth = (month) => {
    const resolvedMonth = resolveMonth(month);
    setSelectedMonth(resolvedMonth);

    try {
      window.sessionStorage.setItem(MONTH_KEY, resolvedMonth);
    } catch {
      // Ignore storage failures and keep in-memory state.
    }
  };

  const handleDomainChange = (domain) => {
    const resolved = resolveDomain(domain);
    setActiveDomain(resolved);
    setActiveTab(0);

    try {
      window.sessionStorage.setItem(DOMAIN_KEY, resolved);
    } catch {
      // Ignore storage failures.
    }
  };

  const handleAuthenticated = (target = AUTH_TARGETS.app) => {
    const resolvedTarget = target === AUTH_TARGETS.cooDashboard ? AUTH_TARGETS.cooDashboard : AUTH_TARGETS.app;

    try {
      window.sessionStorage.setItem(AUTH_KEY, 'true');
      window.sessionStorage.setItem(AUTH_TARGET_KEY, resolvedTarget);
      window.sessionStorage.setItem(MONTH_KEY, selectedMonth);
    } catch {
      // Ignore storage failures and keep in-memory auth state.
    }

    if (resolvedTarget === AUTH_TARGETS.cooDashboard) {
      window.location.assign(COO_DASHBOARD_PATH);
      return;
    }

    if (typeof window !== 'undefined' && window.location.pathname !== '/') {
      window.location.assign('/');
      return;
    }

    setSelectedMonth(resolveMonth(selectedMonth));
    setActiveTab(0);
    setIsAuthenticated(true);
  };

  const handleMonthChange = (month) => {
    persistMonth(month);

    // Feb '26 auto-routes to Tower PM Penalty — the month where tower
    // PM compliance needs direct attention. Saves the COO a click.
    if (month === "Feb'26") {
      setActiveDomain('tower');
      setActiveTab(1); // index 1 = 'PM PENALTY' in the Tower domain tabs
      try {
        window.sessionStorage.setItem(DOMAIN_KEY, 'tower');
      } catch {
        // Ignore storage failures.
      }
    }
  };

  const handleSignOut = () => {
    try {
      window.sessionStorage.removeItem(AUTH_KEY);
      window.sessionStorage.removeItem(AUTH_TARGET_KEY);
      window.sessionStorage.removeItem(MONTH_KEY);
      window.sessionStorage.removeItem(DOMAIN_KEY);
    } catch {
      // Ignore storage failures and reset local state.
    }

    setSelectedMonth(DEFAULT_MONTH);
    setActiveDomain(DEFAULT_DOMAIN);
    setActiveTab(0);
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return <Login onAuthenticated={handleAuthenticated} />;
  }

  return (
    <div className="min-h-screen bg-app-bg">
      <Header selectedMonth={selectedMonth} onMonthChange={handleMonthChange} onSignOut={handleSignOut} activeDomain={activeDomain} onDomainChange={handleDomainChange} />
      <main className="mx-auto flex max-w-7xl flex-col gap-6 px-4 pb-14 pt-6 sm:px-6 lg:px-8">
        <SummaryBand activeDomain={activeDomain} selectedMonth={selectedMonth} />
        {tabs.length > 1 ? (
          <TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
        ) : null}
        <div>{renderTab()}</div>
      </main>

      {/* Drishti bubble — floats bottom-right over every domain view. */}
      <BubbleShell enabled />
    </div>
  );
}

export default App;
